import React, { useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/useToast";
import type { BuildReportInput } from "./diagnosticReportData";
import { downloadDiagnosticReport } from "./downloadDiagnosticReport";

interface DownloadReportButtonProps {
  /**
   * Built at click time, not at render time — the fill screen exports the
   * coach's live, unsaved answers, so the report has to read the state as it
   * stands when the button is pressed.
   */
  report: () => BuildReportInput;
  label?: string;
}

/**
 * The one control that generates a diagnostic report.
 *
 * Local busy state and a failure toast are justified here in a way they are not
 * for an API call: a stalled render is not an HTTP error, so the global axios
 * interceptor never sees it, and the render takes long enough that a button
 * with no feedback reads as broken.
 */
export const DownloadReportButton: React.FC<DownloadReportButtonProps> = ({
  report,
  label = "PDF",
}) => {
  const [preparing, setPreparing] = useState(false);

  const handleClick = () => {
    setPreparing(true);
    // `report()` runs *inside* the chain. Called outside it, a synchronous
    // throw — a null attempt, a field the mapper walks into — escaped
    // `handleClick` before `.finally()` was ever attached, so the button was
    // left disabled on "Preparing…" for the rest of the session with no toast
    // and nothing short of a reload to recover it. Every path settles now:
    // building the report, rendering it, and handing over the blob alike.
    Promise.resolve()
      .then(() => downloadDiagnosticReport(report()))
      .catch(() => {
        toast({
          title: "Error",
          description: "We could not generate that report. Please try again.",
          variant: "destructive",
        });
      })
      .finally(() => setPreparing(false));
  };

  return (
    <Button
      disabled={preparing}
      onClick={handleClick}
      size="sm"
      type="button"
      variant="ghost"
    >
      <Download aria-hidden="true" className="mr-1 h-4 w-4" />
      {preparing ? "Preparing…" : label}
    </Button>
  );
};

export default DownloadReportButton;
